import { Typography, Switch, FormControlLabel, Box } from '@mui/material';
import { useState, useEffect } from 'react';
import useApiError from 'packages/novous_web/src/api/useApiError';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import Button from 'packages/novous_web/src/common/components/Button';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import Loader from 'packages/novous_web/src/common/components/Loader';

interface AlertControlSectionProps {
  selectedCompany?: any;
  selectedUser: any;
}

export default function AlertControlSection({ selectedCompany, selectedUser }: AlertControlSectionProps) {
  const [alertControlEnabled, setAlertControlEnabled] = useState<boolean>(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    data: alarmInvalidData,
    loading: alarmInvalidLoading,
    error: alarmInvalidError,
    fetchData: fetchAlarmInvalidData,
  } = useApiError<any>(`${API_ENDPOINTS.users.ALARM_INVALID_SEND_REQUEST}`, 'POST');

  const {
    data: liveConfigData,
    loading: liveConfigLoading,
    error: liveConfigError,
    fetchData: fetchLiveConfigData,
  } = useAxios<any>(`${API_ENDPOINTS.users.ALARM_INVALID_CHECK_LIVE_CONFIG}`, 'POST');

  useEffect(() => {
    if (alarmInvalidData) {
      setToast({
        open: true,
        message: alarmInvalidData.message || 'Alert Control Settings Saved Successfully',
        type: 'success',
      });
      setIsLoading(false);
    }
  }, [alarmInvalidData]);

  useEffect(() => {
    if (alarmInvalidError) {
      setToast({
        open: true,
        message: 'Error saving alert control settings',
        type: 'error',
      });
      setIsLoading(false);
    }
  }, [alarmInvalidError]);

  useEffect(() => {
    if (liveConfigData) {
      const configValue = liveConfigData.data || liveConfigData;
      setAlertControlEnabled(configValue === 'true' || configValue === true);
    }
  }, [liveConfigData]);

  useEffect(() => {
    if (liveConfigError) {
      setToast({
        open: true,
        message: 'Error fetching alert control status',
        type: 'error',
      });
    }
  }, [liveConfigError]);

  useEffect(() => {
    const fleetId = selectedUser?.id;

    if (fleetId) {
      const vehicleId = null;
      fetchLiveConfigData({
        fleetID: fleetId,
        vehicleID: vehicleId,
      });
    }
  }, [selectedUser]);

  const handleAlertControlToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAlertControlEnabled(event.target.checked);
  };

  const handleSave = () => {
    setIsLoading(true);

    const fleetId = selectedUser?.id;

    const payload = {
      vehicleID: null,
      fleetID: fleetId,
      configValue: `${alertControlEnabled}`,
    };

    fetchAlarmInvalidData(payload);
  };

  const handleCancel = () => {
    setAlertControlEnabled(false);
  };

  return (
    <>
      {(alarmInvalidLoading || liveConfigLoading || isLoading) && <Loader />}

      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          backgroundColor: '#fafafa',
        }}
      >
        <Typography
          variant="h6"
          fontSize="18px"
          fontWeight={600}
          lineHeight="36px"
          sx={{ marginBottom: '16px' }}
        >
          Alert & Live Streaming Control
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '20px' }}>
          Control alert functionality for the fleet. Disabling alerts will also disable live streaming and VOD
          request capabilities.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight={500}>
                Enable Alert Notifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Allow alert, live streaming and VOD for the user
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
              <Switch
                checked={alertControlEnabled}
                onChange={handleAlertControlToggle}
                color="primary"
                disabled={!selectedUser}
              />
            </Box>
            <Box sx={{ flex: 1 }} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <CustomIconButton
            text="Cancel"
            background="transparent"
            outline={true}
            padding="0px"
            variant="outlined"
            onClick={handleCancel}
          />
          <Button text="Save" onClick={handleSave} fullWidth={false} disabled={isLoading || !selectedUser} />
        </Box>
      </Box>

      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type as any}
      />
    </>
  );
}
